import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '../../components/Header/Header';
import StatCard from '../../components/StatCard/StatCard';
import ConversationCard from '../../components/ConversationCard/ConversationCard';
import RelanceCard from '../../components/RelanceCard/RelanceCard';
import { MessageCircle, Send, TrendingUp, Clock, BarChart3, Activity, AlertCircle, RefreshCw } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import analyticsService from '../../api/services/analyticsService';
import conversationService from '../../api/services/conversationService';
import agentService from '../../api/services/agentService';
import productService from '../../api/services/productService';
import orderService from '../../api/services/orderService';
import reminderService from '../../api/services/reminderService';
import { computeHourlyActivity, computeWeeklyActivity } from '../../utils/dashboardCharts';
import { useLang } from '../../i18n/LanguageContext';

const unwrap = (body) => (body && body.data !== undefined ? body.data : body);

const formatReminderTime = (iso, lang) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString(lang === 'en' ? 'en-US' : 'fr-FR', {
    weekday: 'short', day: 'numeric', month: 'short',
    hour: '2-digit', minute: '2-digit',
  });
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, lang } = useLang();

  // ── Requêtes parallèles ──────────────────────────────────────────────────────
  const { data: convStatsData, isLoading: convLoading, isError: convError, refetch: refetchConvStats } = useQuery({
    queryKey: ['conversations', 'all'],
    queryFn: () => conversationService.getConversations({ limit: 100 }),
  });

  const { data: agentsData } = useQuery({
    queryKey: ['agents'],
    queryFn: () => agentService.getAgents(),
  });

  const { data: productsData } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts(),
  });

  const { data: ordersData } = useQuery({
    queryKey: ['orders'],
    queryFn: () => orderService.getOrders({ limit: 1000 }),
  });

  const { data: remindersData } = useQuery({
    queryKey: ['reminders', 'pending'],
    queryFn: () => reminderService.getReminders({ status: 'pending' }),
  });

  const { data: analyticsData } = useQuery({
    queryKey: ['analytics-stats'],
    queryFn: () => analyticsService.getDashboardStats(),
  });

  const { data: recentConvsData, isFetching: recentFetching } = useQuery({
    queryKey: ['conversations-recent'],
    queryFn: () => conversationService.getConversations({ limit: 4, sort: '-createdAt' }),
  });

  // ── Stats dérivées ───────────────────────────────────────────────────────────
  const allConvs = useMemo(() => {
    if (!convStatsData?.data) return [];
    return Array.isArray(convStatsData.data)
      ? convStatsData.data
      : (convStatsData.data.conversations || []);
  }, [convStatsData]);

  const recentConvs = useMemo(() => {
    if (!recentConvsData?.data) return [];
    return Array.isArray(recentConvsData.data)
      ? recentConvsData.data
      : (recentConvsData.data.conversations || []);
  }, [recentConvsData]);

  const reminders = useMemo(() => {
    const list = unwrap(remindersData);
    if (!Array.isArray(list)) return [];
    return list.filter((r) => r.status === 'pending').slice(0, 4);
  }, [remindersData]);

  const stats = useMemo(() => {
    const activeConversations = allConvs.filter(c => c.status === 'active').length;
    const pendingFollowUps = allConvs.filter(c => c.status === 'pending').length;
    const resolvedConversations = allConvs.filter(c => c.status === 'closed' || c.status === 'resolved').length;
    const totalConvs = activeConversations + pendingFollowUps + resolvedConversations;
    const responseRate = totalConvs > 0 ? Math.round((resolvedConversations / totalConvs) * 100) : 0;
    const pendingReminders = reminders.length;

    const d = t.dashboard.stats;
    return [
      {
        icon: MessageCircle,
        title: d.activeConversations,
        value: activeConversations.toString(),
        period: d.today,
        detail: totalConvs > 0 ? d.totalConversations(totalConvs) : d.noConversation,
        delay: 0,
      },
      {
        icon: Send,
        title: d.pendingReminders,
        value: pendingReminders.toString(),
        period: d.inProgress,
        detail: pendingReminders === 0 ? d.noReminder : d.remindersToProcess,
        delay: 0.1,
      },
      {
        icon: TrendingUp,
        title: d.responseRate,
        value: `${responseRate}%`,
        period: d.thisMonth,
        detail: totalConvs > 0 ? d.resolved(resolvedConversations, totalConvs) : d.noData,
        delay: 0.2,
      },
      {
        icon: Clock,
        title: d.avgResponseTime,
        value: '2h15',
        period: d.last7Days,
        detail: d.avgResponseDetail,
        delay: 0.3,
      },
    ];
  }, [allConvs, reminders, t]);

  const dashboardStats = analyticsData?.success ? analyticsData.data : null;

  const chartData = useMemo(() => {
    if (dashboardStats?.weeklyData?.length > 0) return dashboardStats.weeklyData;
    return computeWeeklyActivity(allConvs);
  }, [dashboardStats, allConvs]);

  const activityData = useMemo(() => {
    if (dashboardStats?.hourlyActivity?.length > 0) return dashboardStats.hourlyActivity;
    return computeHourlyActivity(allConvs);
  }, [dashboardStats, allConvs]);

  const formatRelativeTime = (date) => {
    if (!date) return t.common.unknown;
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return t.common.justNow;
    if (diffMins < 60) return t.common.relativeMin(diffMins);
    if (diffHours < 24) return t.common.relativeHour(diffHours);
    return t.common.relativeDay(diffDays);
  };

  // ── Premier chargement (aucun cache) ─────────────────────────────────────────
  if (convLoading) {
    return (
      <div>
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div
              className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
              style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }}
            />
            <p className="text-label">{t.dashboard.loadingText}</p>
          </div>
        </div>
      </div>
    );
  }

  if (convError && !convStatsData) {
    return (
      <div>
        <Header />
        <div className="card p-5 mb-4" style={{ borderColor: 'rgba(224,84,84,0.3)' }}>
          <div className="flex items-start gap-3">
            <AlertCircle size={18} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>{t.common.error}</p>
              <button onClick={() => refetchConvStats()} className="btn-primary mt-3" style={{ height: '32px', fontSize: '12px' }}>
                {t.common.retry}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />

      {/* Greeting row */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              {t.dashboard.title}
            </p>
          </div>
          {recentFetching && (
            <RefreshCw size={13} className="animate-spin" style={{ color: 'var(--c-text-muted)' }} />
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, index) => (
          <StatCard
            key={index}
            icon={stat.icon}
            title={stat.title}
            value={stat.value}
            period={stat.period}
            detail={stat.detail}
            delay={stat.delay}
          />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {/* Chart Conversations hebdomadaire */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-medium text-ink flex items-center gap-2">
                <BarChart3 size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
                {t.dashboard.charts.weeklyActivity}
              </h3>
              <p className="text-label mt-0.5">{t.dashboard.charts.conversationsAndReminders}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: 'rgb(var(--c-accent-green-rgb))' }} />
                <span className="text-label">{t.dashboard.charts.conversations}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full" style={{ background: 'var(--c-info)' }} />
                <span className="text-label">{t.dashboard.charts.reminders}</span>
              </div>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 6" stroke="var(--c-border)" strokeOpacity={0.6} />
              <XAxis dataKey="name" tick={{ fill: 'var(--c-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--c-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontSize: '13px' }}
                labelStyle={{ color: 'var(--c-text-light)', fontWeight: 500, marginBottom: '6px' }}
                cursor={{ stroke: 'rgb(var(--c-accent-green-rgb))', strokeWidth: 1, strokeDasharray: '4 4' }}
              />
              <defs>
                <linearGradient id="lineGreen" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgb(79,179,103)" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="rgb(79,179,103)" stopOpacity={0.6} />
                </linearGradient>
                <linearGradient id="lineBlue" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#4A90D9" stopOpacity={0.9} />
                  <stop offset="100%" stopColor="#4A90D9" stopOpacity={0.6} />
                </linearGradient>
              </defs>
              <Line type="monotone" dataKey="conversations" stroke="url(#lineGreen)" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: 'rgb(79,179,103)', stroke: 'var(--c-primary-card)', strokeWidth: 2 }} animationDuration={800} />
              <Line type="monotone" dataKey="relances" stroke="url(#lineBlue)" strokeWidth={2} dot={false} activeDot={{ r: 5, fill: '#4A90D9', stroke: 'var(--c-primary-card)', strokeWidth: 2 }} animationDuration={1000} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Chart Activité 24h */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-base font-medium text-ink flex items-center gap-2">
                <Activity size={16} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
                {t.dashboard.charts.activity24h}
              </h3>
              <p className="text-label mt-0.5">{t.dashboard.charts.hourlyVolume}</p>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="status-dot status-online" />
              <span className="text-label">{t.dashboard.charts.realtime}</span>
            </div>
          </div>

          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={activityData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 6" stroke="var(--c-border)" strokeOpacity={0.6} />
              <XAxis dataKey="name" tick={{ fill: 'var(--c-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'var(--c-text-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: 'var(--c-primary-card)', border: '0.5px solid var(--c-border)', borderRadius: 'var(--radius-lg)', padding: '12px 16px', boxShadow: '0 8px 24px rgba(0,0,0,0.2)', fontSize: '13px' }}
                labelStyle={{ color: 'var(--c-text-light)', fontWeight: 500, marginBottom: '6px' }}
                cursor={{ stroke: 'rgb(var(--c-accent-green-rgb))', strokeWidth: 1, strokeDasharray: '4 4' }}
              />
              <defs>
                <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgb(79,179,103)" stopOpacity={0.25} />
                  <stop offset="100%" stopColor="rgb(79,179,103)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area type="monotone" dataKey="value" stroke="rgb(79,179,103)" strokeWidth={2} fill="url(#areaFill)" dot={false} activeDot={{ r: 5, fill: 'rgb(79,179,103)', stroke: 'var(--c-primary-card)', strokeWidth: 2 }} animationDuration={800} />
            </AreaChart>
          </ResponsiveContainer>

          <div className="grid grid-cols-3 gap-3 mt-4 pt-4" style={{ borderTop: '0.5px solid var(--c-border)' }}>
            <div className="text-center">
              <div className="font-metric text-ink" style={{ fontSize: '20px', fontWeight: 600 }}>
                {activityData.reduce((max, item) => Math.max(max, item.value), 0)}
              </div>
              <div className="text-label mt-0.5">{t.dashboard.charts.peak}</div>
            </div>
            <div className="text-center">
              <div className="font-metric" style={{ fontSize: '20px', fontWeight: 600, color: 'rgb(var(--c-accent-green-rgb))' }}>
                {activityData.length > 0 ? Math.round(activityData.reduce((s, i) => s + i.value, 0) / activityData.length) : 0}
              </div>
              <div className="text-label mt-0.5">{t.dashboard.charts.average}</div>
            </div>
            <div className="text-center">
              <div className="font-metric text-ink" style={{ fontSize: '20px', fontWeight: 600 }}>
                {activityData.filter(item => item.value > 0).length}
              </div>
              <div className="text-label mt-0.5">{t.dashboard.charts.activeHours}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Conversations and Relances Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Conversations */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-ink">{t.dashboard.recentConversations}</h3>
            <button className="btn-ghost" style={{ height: '28px', padding: '0 12px', fontSize: '12px' }}>
              {t.common.seeAll}
            </button>
          </div>
          <div className="space-y-3">
            {recentConvs.length > 0 ? (
              recentConvs.map((conv) => (
                <ConversationCard
                  key={conv.id || conv._id}
                  name={conv.customerName || conv.customer?.name || t.dashboard.unknownClient}
                  message={
                    typeof conv.lastMessage === 'string'
                      ? conv.lastMessage
                      : conv.lastMessage?.content || conv.messages?.[conv.messages.length - 1]?.content || t.dashboard.noMessage
                  }
                  status={conv.status === 'active' ? t.dashboard.statusWaiting : conv.status === 'resolved' ? t.dashboard.statusDone : conv.status}
                  time={formatRelativeTime(conv.updatedAt || conv.createdAt)}
                />
              ))
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">{t.dashboard.noRecentConversations}</p>
            )}
          </div>
        </div>

        {/* Relances */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-medium text-ink">{t.dashboard.scheduledReminders}</h3>
            <button onClick={() => navigate('/relances')} className="btn-ghost" style={{ height: '28px', padding: '0 12px', fontSize: '12px' }}>
              {t.common.seeAll}
            </button>
          </div>
          <div className="space-y-3">
            {reminders.length > 0 ? (
              reminders.map((reminder) => (
                <RelanceCard
                  key={reminder._id}
                  name={reminder.clientName || t.relances.client}
                  task={reminder.reason || t.relances.title}
                  time={formatReminderTime(reminder.triggerAt, lang)}
                />
              ))
            ) : (
              <p className="text-gray-400 text-sm text-center py-4">{t.dashboard.noScheduledReminders}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
